import axios from "axios";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CandidatePersonalForm from "@/Components/candidate/CandidatePersonalForm";
import ModalPopUp from "@/Components/common/ModalPopUp";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import { PYTHON_SERVICE_MAIN_URL } from "@/Constants/common";
import { routingConstants } from "@/Constants/routingConstants";
import {
  handleCandidateDetailsSubmit,
  toggleResumeUploaded,
  updateCandidateCreateData,
  updateCandidateEditData,
} from "@/reducers/candidateCreate";
import { fetchCandidateDropdownData } from "@/reducers/clientcommon";
import useTitle from "@/utilities/hooks/useTitle";
import { apiEncrypt } from "@/utilities/keyEncryption";
import { validateCandidatePersonalForm } from "@/validations/candidatePersonal";

// Dynamically import PDF.js and NLP libraries
let pdfjsLib = null;
let nlp = null;
let Fuse = null;

import("pdfjs-dist").then((pdfjs) => {
  pdfjsLib = pdfjs;
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
});
import("compromise").then((compromise) => {
  nlp = compromise.default || compromise;
});
import("fuse.js").then((fuseModule) => {
  Fuse = fuseModule.default || fuseModule;
});

const CandidateCreate = () => {
  useTitle("Create Candidate");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get createData from Redux
  const { createData } = useSelector((state) => state.candidateCreate);
  const clientUserAllPermissions = useSelector(
    (state) => state.session.clientUserAllPermissions,
  );
  const canViewCompliance =
    clientUserAllPermissions?.is_allow_to_upload_candidate_compliance !== 0;

  useEffect(() => {
    dispatch(updateCandidateCreateData(null));
    dispatch(updateCandidateEditData(null));
  }, []);

  useEffect(() => {
    dispatch(fetchCandidateDropdownData());
  }, []);

  const [formSubmitted, setFormSubmitted] = useState({
    personal: false,
    payment: false,
    compliance: false,
  });

  const [showImportResumeModal, setShowImportResumeModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [extractedData, setExtractedData] = useState(null);

  function nlpFallbackData(nlpData, extractedText) {
    return {
      name: nlpData.names?.[0] || "",
      email: nlpData.emails?.[0] || "",
      phone: nlpData.phones?.[0] || "",
      raw_text: extractedText,
      confidence_scores: {
        name: nlpData.names?.length > 0 ? 0.6 : 0,
        email: nlpData.emails?.length > 0 ? 0.8 : 0,
        phone: nlpData.phones?.length > 0 ? 0.7 : 0,
      },
    };
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (PDF, DOC, DOCX)
      const allowedTypes = ["application/pdf"];
      if (allowedTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        alert("Please select a valid PDF file");
        event.target.value = "";
      }
    }
  };

  // Extract text from PDF files
  const extractTextFromPDF = async (file) => {
    try {
      setProcessingStatus("Loading PDF document...");

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer,
        maxImageSize: 1024 * 1024 * 10,
        disableFontFace: false,
        disableRange: false,
        disableStream: false,
      }).promise;

      setProcessingStatus(`Processing ${pdf.numPages} pages...`);

      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        setProcessingStatus(
          `Extracting text from page ${i} of ${pdf.numPages}...`,
        );

        try {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent({
            normalizeWhitespace: true,
            disableCombineTextItems: false,
          });

          if (textContent.items.length === 0) {
            fullText += `Page ${i}:\n[No extractable text found]\n\n`;
            continue;
          }

          // Sort text items by position
          const sortedItems = textContent.items
            .filter((item) => item.str && item.str.trim().length > 0)
            .map((item) => ({
              text: item.str.trim(),
              x: item.transform[4],
              y: item.transform[5],
            }))
            .sort((a, b) => {
              const yDiff = Math.abs(b.y - a.y);
              if (yDiff > 3) {
                return b.y - a.y;
              }
              return a.x - b.x;
            });

          // Group items by lines
          const lines = [];
          let currentLine = [];
          let lastY = null;
          const lineThreshold = 5;

          sortedItems.forEach((item) => {
            const currentY = Math.round(item.y);
            if (lastY === null || Math.abs(currentY - lastY) <= lineThreshold) {
              currentLine.push(item.text);
            } else {
              if (currentLine.length > 0) {
                const lineText = currentLine
                  .join(" ")
                  .replace(/\s+/g, " ")
                  .trim();
                if (lineText.length > 0) {
                  lines.push(lineText);
                }
              }
              currentLine = [item.text];
            }
            lastY = currentY;
          });

          // Add the last line
          if (currentLine.length > 0) {
            const lineText = currentLine.join(" ").replace(/\s+/g, " ").trim();
            if (lineText.length > 0) {
              lines.push(lineText);
            }
          }

          const pageText = lines.join("\n");
          if (pageText.length > 0) {
            fullText += `Page ${i}:\n${pageText}\n\n`;
          }
        } catch (pageError) {
          fullText += `Page ${i}:\n[Error extracting text from this page]\n\n`;
        }
      }

      setProcessingStatus("PDF extraction completed");

      if (fullText.trim().length === 0) {
        throw new Error(
          "No text could be extracted from the PDF. The PDF might be image-based or corrupted.",
        );
      }

      return fullText;
    } catch (error) {
      setProcessingStatus("");
      throw new Error("Failed to extract text from PDF: " + error.message);
    }
  };

  // Extract text from other file types
  const extractTextFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };

  // NLP analysis using Compromise
  const analyzeWithNLP = (text) => {
    if (!nlp) {
      return extractBasicInfo(text);
    }

    try {
      const doc = nlp(text);

      return {
        people: doc.people().out("array") || [],
        places: doc.places().out("array") || [],
        organizations: doc.organizations().out("array") || [],
        emails: doc.emails().out("array") || [],
        phoneNumbers: doc.phoneNumbers().out("array") || [],
        dates: doc.dates().out("array") || [],
        ...extractBasicInfo(text),
      };
    } catch (error) {
      return extractBasicInfo(text);
    }
  };

  // Basic info extraction using regex patterns
  const extractBasicInfo = (text) => {
    const patterns = {
      emails: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      phones:
        /(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/g,
      names: /^([A-Z][a-z]+(?:\s+[A-Z]\.?\s*)?[A-Z][a-z]+)/m,
    };

    const results = {};
    for (const [key, pattern] of Object.entries(patterns)) {
      const matches = text.match(pattern) || [];
      results[key] = [...new Set(matches)].filter(
        (match) => match && match.trim().length > 0,
      );
    }

    return results;
  };

  // Process CV with Python service
  const processCVWithPythonService = async (text) => {
    try {
      setProcessingStatus("Sending to AI parser...");

      const payload = {
        raw_text: text,
        file_name: selectedFile.name,
      };

      const response = await axios.post(
        `${PYTHON_SERVICE_MAIN_URL}/parse-extracted-cv`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        },
      );

      // Check if response has data
      if (response && response.data) {
        return response.data;
      } else {
        throw new Error("No data received from Python service");
      }
    } catch (error) {
      let errorMessage = "Failed to process CV with Python service";
      if (
        error.code === "ECONNREFUSED" ||
        error.message.includes("Network Error")
      ) {
        errorMessage =
          "Python service is not running. Please start the service at http://192.168.1.29:8001";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // ============================================================================
  // SECTION 2: Fixed autoFillReduxStore Function
  // ============================================================================

  const autoFillReduxStore = (data) => {
    try {
      const reduxData = {};
      const confidenceScores = data.confidence_scores || {};
      Object.entries(confidenceScores).forEach(([key, score]) => {
        console.log(`   ${key}: ${Math.round(score * 100)}%`);
      });

      // Helper function with clear logging
      const setFieldIfConfident = (
        value,
        confidenceKey,
        reduxKey,
        threshold = 0.75,
      ) => {
        const confidence = confidenceScores[confidenceKey] || 0;

        if (value && value.toString().trim() && confidence >= threshold) {
          reduxData[reduxKey] = value;
          return true;
        } else {
          if (!value || !value.toString().trim()) {
            console.log(`   ❌ FAILED - Empty value`);
          } else {
            console.log(
              `   ❌ FAILED - Low confidence (${Math.round(confidence * 100)}% < ${Math.round(threshold * 100)}%)`,
            );
          }
          return false;
        }
      };
      // Process name fields (all use 'name' confidence)
      if (data.candidate_title) {
        setFieldIfConfident(data.candidate_title, "name", "title");
      }

      if (data.firstName || data.candidate_forename) {
        const firstName = data.firstName || data.candidate_forename;
        setFieldIfConfident(firstName, "name", "firstName");
      }

      if (data.lastName || data.candidate_surname) {
        const lastName = data.lastName || data.candidate_surname;
        setFieldIfConfident(lastName, "name", "lastName");
      }

      // Fallback: split full name if components aren't available
      if (!reduxData.firstName && !reduxData.lastName && data.name) {
        const nameConfidence = confidenceScores.name || 0;

        if (nameConfidence >= 0.75) {
          const nameParts = data.name.split(" ").filter((p) => p.trim());
          if (nameParts.length >= 2) {
            reduxData.firstName = nameParts[0];
            reduxData.lastName = nameParts.slice(1).join(" ");
          }
        }
      }

      // Process contact fields
      if (data.email || data.candidate_email) {
        const email = data.email || data.candidate_email;
        setFieldIfConfident(email, "email", "email");
      }

      if (data.phone || data.mobile_phone_number) {
        const phone = data.phone || data.mobile_phone_number;
        setFieldIfConfident(phone, "phone", "mobile");
      }

      if (data.address) {
        setFieldIfConfident(data.address, "address", "address");
      }

      if (data.postcode || data.candidate_postcode) {
        const postcode = data.postcode || data.candidate_postcode;
        setFieldIfConfident(postcode, "postcode", "postCode");
      }

      // Process other fields
      if (data.dob || data.dob) {
        const dob = data.dob || data.dob;
        setFieldIfConfident(dob, "dob", "dateOfBirth");
      }

      if (data.gender || data.candidate_gender) {
        const gender = data.gender || data.candidate_gender;
        setFieldIfConfident(gender, "gender", "gender");
      }

      if (data.gmc_number) {
        setFieldIfConfident(data.gmc_number, "gmc_number", "gmcNumber");
      }

      // Process skills array
      if (data.skills && Array.isArray(data.skills) && data.skills.length > 0) {
        const skillsConfidence = confidenceScores.skills || 0;

        if (skillsConfidence >= 0.75) {
          const validSkills = data.skills.filter(
            (skill) => skill && skill.trim(),
          );
          if (validSkills.length > 0) {
            reduxData.skills = validSkills;
          }
        } else {
          console.log(`   ❌ FAILED - Low confidence`);
        }
      }

      // Process location
      if (data.candidate_lat && data.candidate_lng) {
        const locationConfidence =
          confidenceScores.location || confidenceScores.address || 0;
        if (locationConfidence >= 0.75) {
          reduxData.clientLat = data.candidate_lat;
          reduxData.clientLng = data.candidate_lng;
        }
      }

      // Dispatch to Redux
      if (Object.keys(reduxData).length > 0) {
        dispatch(updateCandidateCreateData(reduxData));
        dispatch(toggleResumeUploaded(true));

        const updatedFields = Object.keys(reduxData).join(", ");
        // setTimeout(() => {
        //   alert(`✅ CV data successfully extracted!\n\nUpdated ${Object.keys(reduxData).length} fields:\n${updatedFields}\n\nOnly fields with confidence ≥ 75% were filled.`);
        // }, 500);
      } else {
        console.log("❌ No fields passed confidence threshold");

        // Show which fields were close
        const closeFields = Object.entries(confidenceScores)
          .filter(([_, conf]) => conf >= 0.5 && conf < 0.75)
          .map(([key, conf]) => `${key} (${Math.round(conf * 100)}%)`)
          .join(", ");

        const message = closeFields
          ? `CV processed, but no fields met the 75% confidence threshold.\n\nClose matches (50-74%):\n${closeFields}\n\nPlease review and fill the form manually.`
          : "CV processed, but no fields met the confidence threshold of 75%. Please review and fill the form manually.";

        setTimeout(() => {
          alert(message);
        }, 500);
      }
    } catch (error) {
      console.error("❌ Error in autoFillReduxStore:", error);
      console.error("Stack trace:", error.stack);
      alert("Error auto-filling form: " + error.message);
    }
  };

  const handleImportResume = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setProcessingStatus("Starting CV processing...");

    try {
      // Step 1: Extract text from file
      let extractedText = "";
      if (
        selectedFile.type === "application/pdf" ||
        selectedFile.name.endsWith(".pdf")
      ) {
        extractedText = await extractTextFromPDF(selectedFile);
      } else {
        extractedText = await extractTextFromFile(selectedFile);
      }

      // Step 2: Analyze with NLP
      setProcessingStatus("Analyzing with NLP...");
      const nlpData = analyzeWithNLP(extractedText);

      // Step 3: Process with Python service (if available)
      let pythonData = null;
      try {
        pythonData = await processCVWithPythonService(extractedText);
      } catch (pythonError) {
        console.warn(
          "🐍 Python service failed, using NLP data only:",
          pythonError.message,
        );
        console.error("🐍 Python error details:", pythonError);
      }

      let finalData = {};

      if (pythonData) {
        // Handle both direct array and wrapped data formats
        let dataArray = pythonData;
        if (pythonData && pythonData.data && Array.isArray(pythonData.data)) {
          dataArray = pythonData.data;
        } else if (!Array.isArray(pythonData)) {
          dataArray = [pythonData];
        }

        if (dataArray && Array.isArray(dataArray)) {
          // Process each item in the Python response array
          const processedData = {
            skills: [],
            education: [],
            experience: [],
            confidence_scores: {},
          };

          Object.values(dataArray[0]).forEach((item, index) => {
            const { type, field, value, confidence } = item;

            // CRITICAL FIX: Map field names to standardized confidence keys
            const confidenceKeyMap = {
              // Personal info
              name: "name",
              candidate_title: "name",
              firstName: "name",
              lastName: "name",
              forename: "name",
              surname: "name",

              // Contact info
              candidate_email: "email",
              email: "email",
              mobile_phone_number: "phone",
              phone: "phone",
              address: "address",
              postcode: "postcode",

              // Skills
              candidate_skill_ids: "skills",
              skill: "skills",

              // Education
              education_qualification: "education",
              degree: "education",

              // Experience
              work_experience: "experience",

              // Summary
              professional_summary: "summary",

              // Other
              gmc_number: "gmc_number",
              dob: "dob",
              gender: "gender",
            };

            // Store confidence with standardized key
            if (confidence !== undefined && confidenceKeyMap[field]) {
              const standardKey = confidenceKeyMap[field];
              // For skills, use the highest confidence
              if (standardKey === "skills") {
                processedData.confidence_scores[standardKey] = Math.max(
                  processedData.confidence_scores[standardKey] || 0,
                  confidence,
                );
              } else {
                processedData.confidence_scores[standardKey] = confidence;
              }
            }

            // Process ALL items regardless of confidence (we'll filter in autoFillReduxStore)
            // Store the actual values
            switch (type) {
              case "personal_info":
                if (field === "name") {
                  processedData.name = value;
                } else if (field === "candidate_title" || field === "title") {
                  processedData.candidate_title = value;
                } else if (field === "firstName" || field === "forename") {
                  processedData.candidate_forename = value;
                  processedData.firstName = value;
                } else if (field === "lastName" || field === "surname") {
                  processedData.candidate_surname = value;
                  processedData.lastName = value;
                } else if (field === "dob") {
                  processedData.dob = value;
                } else if (field === "gmc_number") {
                  processedData.gmc_number = value;
                }
                break;

              case "contact_info":
                if (field === "candidate_email" || field === "email") {
                  processedData.candidate_email = value;
                  processedData.email = value;
                } else if (
                  field === "mobile_phone_number" ||
                  field === "phone"
                ) {
                  processedData.mobile_phone_number = value;
                  processedData.phone = value;
                } else if (field === "address") {
                  processedData.address = value;
                } else if (field === "postcode") {
                  processedData.candidate_postcode = value;
                  processedData.postcode = value;
                }
                break;

              case "skills":
                if (field === "candidate_skill_ids" || field === "skill") {
                  processedData.skills.push(value);
                }
                break;

              case "education":
                if (field === "education_qualification" || field === "degree") {
                  const educationItem = {
                    degree: value,
                    ...item.details,
                  };
                  processedData.education.push(educationItem);
                }
                break;

              case "experience":
                if (field === "work_experience") {
                  processedData.experience.push({
                    title: value,
                    ...item.details,
                  });
                }
                break;

              case "summary":
                if (field === "professional_summary") {
                  processedData.summary = value;
                }
                break;

              default:
                processedData[field] = value;
            }
          });

          // Create full name if components exist
          if (
            processedData.candidate_forename &&
            processedData.candidate_surname
          ) {
            const fullName =
              `${processedData.candidate_title ? processedData.candidate_title + " " : ""}${processedData.candidate_forename} ${processedData.candidate_surname}`.trim();
            processedData.name = fullName;
          }

          // Add raw text for reference
          processedData.raw_text = extractedText;

          // Log which fields will pass the threshold
          Object.entries(processedData.confidence_scores).forEach(
            ([key, conf]) => {
              console.log(
                `   ${key}: ${Math.round(conf * 100)}% ${conf >= 0.75 ? "✅" : "❌"}`,
              );
            },
          );

          finalData = processedData;
        } else {
          finalData = nlpFallbackData(nlpData, extractedText);
        }
      } else {
        finalData = nlpFallbackData(nlpData, extractedText);
      }

      setExtractedData(finalData);

      // Step 5: Auto-fill Redux store
      autoFillReduxStore(finalData);

      setProcessingStatus("CV processed successfully!");

      // Show success message and close modal after a delay
      setTimeout(() => {
        setShowImportResumeModal(false);
        setSelectedFile(null);
        setProcessingStatus("");
      }, 3000);
    } catch (error) {
      alert("Error processing CV: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="shadow-[0px_1px_8px_0px_#E7EFFB grid gap-3 rounded-[10px] bg-white py-6">
        <div className="flex items-center justify-between px-5">
          <h1 className="text-[20px] font-semibold leading-[normal] text-black">
            Create Candidate
          </h1>
          <div className="flex gap-2">
            <button
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-primaryHover"
              onClick={() => {
                setShowImportResumeModal(true);
              }}
            >
              Upload CV
            </button>
          </div>
        </div>
        <div className="bg-lightblue px-5">
          <div>
            <button className="inline-block border-b-2 border-b-primary px-4 py-2 text-primary">
              Personal
            </button>
            <button className="border-b-transparent inline-block border-b-2 border-b-lightblue px-4 py-2 text-txtDark">
              Payment
            </button>
            {canViewCompliance && (
              <button className="border-b-transparent inline-block border-b-2 border-b-lightblue px-4 py-2 text-txtDark">
                Compliance
              </button>
            )}
          </div>
        </div>
        <div className="px-5">
          {!formSubmitted.personal ? (
            <Formik
              validateOnChange={true}
              validateOnBlur={true}
              initialValues={createData} // Use Redux createData as initial values
              enableReinitialize={true} // This will reinitialize when createData changes
              validate={validateCandidatePersonalForm}
              onSubmit={(values, { setSubmitting }) => {
                const postData = {
                  candidate_title: values.title,
                  candidate_forename: values.firstName,
                  candidate_surname: values.lastName,
                  candidate_gender: values.gender,
                  mobile_phone_number: values.mobile,
                  candidate_email: values.email,
                  candidate_parent_id: values.setting,
                  candidate_type_id: values.role,
                  candidate_a1: values.address,
                  county_id: values.county,
                  region_id: values.region,
                  candidate_postcode: values.postCode,
                  action: "add", // edit
                  candidate_lat: values.clientLat,
                  candidate_lng: values.clientLng,
                  candidate_dob: values.dateOfBirth,
                  band_id: values.band,
                  compliance_consultant_id: values.consultant,
                  candidate_referer_id: values.referral,
                  gmc_number: values.gmcNumber,
                  candidate_skill_ids: values.skills?.join(","),
                };
                const callback = (candidateId) => {
                  setSubmitting(false);

                  navigate(
                    routingConstants.candidateEditRoute(
                      apiEncrypt(candidateId),
                    ) + "#payment",
                  );
                  // setFormSubmitted((prev) => ({ ...prev, personal: true }));
                };
                dispatch(
                  handleCandidateDetailsSubmit({ ...postData, callback }),
                );
              }}
            >
              {(formikProps) => {
                return <CandidatePersonalForm />;
              }}
            </Formik>
          ) : (
            <>sahd</>
          )}
        </div>
      </div>

      {/* Import Resume Modal */}
      <ModalPopUp
        show={showImportResumeModal}
        onClose={() => {
          setShowImportResumeModal(false);
          setSelectedFile(null);
          setExtractedData(null);
          setProcessingStatus("");
        }}
        panelClass="!w-full !max-w-[450px] !mb-0 h-auto !p-0"
        panelContainerClass="flex justify-center items-center"
      >
        <div className="w-full">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h3 className="text-[20px] font-semibold leading-[normal] text-black">
              Import Resume
            </h3>
            <button
              onClick={() => {
                setShowImportResumeModal(false);
                setSelectedFile(null);
                setExtractedData(null);
                setProcessingStatus("");
              }}
              className="hover:bg-red-50 rounded p-1 text-red outline-none ring-0"
            >
              <RejectIcon />
            </button>
          </div>

          {/* Modal Body */}
          <div className="px-6 pb-6">
            <div className="rounded-[8px] border-2 border-dashed border-borderGrey bg-grey p-6 text-center">
              <div className="mb-3">
                <svg
                  className="mx-auto h-10 w-10 text-txtColor"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div className="mb-2">
                <label htmlFor="resume-upload" className="cursor-pointer">
                  <span className="text-sm font-medium text-primary">
                    Click to upload
                  </span>
                  <span className="text-sm text-txtColor">
                    {" "}
                    or drag and drop
                  </span>
                </label>
                <input
                  id="resume-upload"
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                />
              </div>
              <p className="text-xs text-txtColor">PDF up to 10MB</p>

              {selectedFile && (
                <div className="mt-4 rounded-[6px] border border-borderGrey bg-white p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg
                        className="mr-2 h-4 w-4 text-txtColor"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="whitespace-normal break-all text-sm font-medium text-black">
                        {selectedFile.name}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="hover:bg-red-50 rounded p-1 text-red"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Processing Status */}
              {isProcessing && (
                <div className="mt-4 rounded-[6px] bg-lightblue p-3">
                  <div className="flex items-center justify-center">
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                    <span className="text-sm font-medium text-primary">
                      {processingStatus || "Processing..."}
                    </span>
                  </div>
                </div>
              )}

              {/* Extracted Data Preview */}
              {extractedData && !isProcessing && (
                <div className="mt-4 rounded-[6px] bg-lightblue p-3 text-left">
                  <h4 className="mb-2 text-sm font-medium text-primary">
                    Extracted Information (Confidence ≥ 75%):
                  </h4>
                  <div className="space-y-1 text-xs">
                    {(() => {
                      const confidenceScores =
                        extractedData.confidence_scores || {};
                      const isConfident = (key, threshold = 0.75) =>
                        (confidenceScores[key] || 0) >= threshold;

                      return (
                        <>
                          {/* Name Information */}
                          {isConfident("name") &&
                            (extractedData.candidate_forename ||
                              extractedData.candidate_surname ||
                              extractedData.name) && (
                              <div className="flex justify-between">
                                <span>
                                  <span className="font-medium">Name:</span>
                                  {extractedData.candidate_title &&
                                    ` ${extractedData.candidate_title}`}
                                  {extractedData.candidate_forename &&
                                    ` ${extractedData.candidate_forename}`}
                                  {extractedData.candidate_surname &&
                                    ` ${extractedData.candidate_surname}`}
                                  {!extractedData.candidate_forename &&
                                    !extractedData.candidate_surname &&
                                    extractedData.name &&
                                    ` ${extractedData.name}`}
                                </span>
                                <span className="text-xs text-green">
                                  {Math.round(
                                    (confidenceScores.name || 0) * 100,
                                  )}
                                  %
                                </span>
                              </div>
                            )}

                          {/* Contact Information */}
                          {isConfident("email") && extractedData.email && (
                            <div className="flex justify-between">
                              <span>
                                <span className="font-medium">Email:</span>{" "}
                                {extractedData.email}
                              </span>
                              <span className="text-xs text-green">
                                {Math.round(
                                  (confidenceScores.email || 0) * 100,
                                )}
                                %
                              </span>
                            </div>
                          )}

                          {isConfident("phone") && extractedData.phone && (
                            <div className="flex justify-between">
                              <span>
                                <span className="font-medium">Phone:</span>{" "}
                                {extractedData.phone}
                              </span>
                              <span className="text-xs text-green">
                                {Math.round(
                                  (confidenceScores.phone || 0) * 100,
                                )}
                                %
                              </span>
                            </div>
                          )}

                          {/* Skills */}
                          {isConfident("skills") &&
                            extractedData.skills &&
                            Array.isArray(extractedData.skills) &&
                            extractedData.skills.length > 0 && (
                              <div className="flex justify-between">
                                <span>
                                  <span className="font-medium">Skills:</span>{" "}
                                  {extractedData.skills.slice(0, 3).join(", ")}
                                  {extractedData.skills.length > 3 &&
                                    ` (+${extractedData.skills.length - 3} more)`}
                                </span>
                                <span className="text-xs text-green">
                                  {Math.round(
                                    (confidenceScores.skills || 0) * 100,
                                  )}
                                  %
                                </span>
                              </div>
                            )}

                          {/* Show all confidence scores for debugging */}
                          <div className="mt-2 border-t border-primary/20 pt-2">
                            <span className="text-xs font-medium text-primary">
                              All Confidence Scores:
                            </span>
                            <div className="mt-1 grid grid-cols-2 gap-1">
                              {Object.entries(confidenceScores).map(
                                ([key, score]) => (
                                  <div
                                    key={key}
                                    className={`text-xs ${score >= 0.75 ? "text-green" : "text-orange"}`}
                                  >
                                    <span className="capitalize">
                                      {key.replace("_", " ")}:
                                    </span>{" "}
                                    {Math.round(score * 100)}%
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImportResumeModal(false);
                  setSelectedFile(null);
                  setExtractedData(null);
                  setProcessingStatus("");
                }}
                className="rounded-[6px] border border-borderGrey bg-white px-4 py-2 text-sm font-medium text-txtColor transition-colors duration-200 hover:bg-grey"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                onClick={handleImportResume}
                disabled={!selectedFile || isProcessing}
                className="rounded-[6px] bg-primary px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-primaryHover disabled:cursor-not-allowed disabled:bg-lightGrey disabled:text-txtColor"
              >
                {isProcessing ? "Processing..." : "Import & Fill Form"}
              </button>
            </div>
          </div>
        </div>
      </ModalPopUp>
    </div>
  );
};

export default CandidateCreate;
